import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControlStatus } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import {
  getExhibitionSettings,
  getSelectedStep,
  getSelfRegistration,
  selectIsTicketAssignmentStepVisible,
  selectIsTicketLimitPerEmailActive
} from '@app/app.reducer';
import { FieldKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import {
  DisableValidationOnSelectedSteps,
  InitTicketLimitPerEmailAddressValidationTrigger
} from '@core/data-access/models/form/form-ticket-limit-validity.model';
import {
  CheckBuyerInformationFormsValidity,
  CheckedTariffEmailAddressValidity,
  CheckMergedTicketAssignmentFormsValidity,
  GroupedNameEmailAddressAndPersonsByTariffUuids
} from '@core/data-access/models/form/form-validity.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { PersonType } from '@core/data-access/models/person.model';
import { FormMediator } from '@core/data-access/services/form/form-mediator.service';
import { selectBuyerInformationEmailAddress } from '@core/features/buyer-information/store/buyer-information.selectors';
import { TicketHolderFormStates } from '@core/features/ticket-assignment/models/ticket-holder-form.model';
import {
  selectGroupedNameEmailAddressAndPersonsByTariffUuids,
  selectSelectedTariffHolderUuid,
  selectSelectedTariffUuid
} from '@core/features/ticket-assignment/store/ticket-assignment.selectors';
import { mapValidatedDailyTicketPerLimitResponseToCheckedValidity } from '@core/utils/data-mappers';
import { validateEmailAddress } from '@core/validators/email-address.validator';
import { select, Store } from '@ngrx/store';
import { ValidateDailyTicketPerEmailLimitBody } from '@products/models/tariff-status.model';
import { TariffUuid, TicketPersonId } from '@products/models/tariff.model';
import { TariffStatusService } from '@products/services/tariff-status.service';
import {
  getAllTariffUuidsFromBookedProducts,
  selectAllTicketHolderTariffUuidsWithTicketPersonId,
  selectTicketHolderBookedTariffsSortedByBookingOrder
} from '@store/products/booking/tariff/booking-tariff.selectors';
import { StepName } from '@store/step-forms/step.interface';
import { mapStepRouteToStepName } from '@store/step-forms/utils/step-validity-mappers';
import {
  map as _map,
  merge as _merge,
  includes,
  isEmpty,
  isEqual,
  isNil,
  isNull,
  last,
  mapValues,
  omitBy,
  reduce,
  some,
  split,
  values
} from 'lodash';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounce,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  iif,
  map,
  merge,
  Observable,
  of,
  pairwise,
  pipe,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  timer,
  UnaryFunction,
  withLatestFrom
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FormTicketLimitValidity {
  readonly #router = inject(Router);
  readonly #store = inject(Store);
  readonly #formMediator = inject(FormMediator);
  readonly #tariffStatusService = inject(TariffStatusService);

  readonly #_isInitialization$ = new BehaviorSubject(true);
  readonly #_isTicketLimitPerEmailAddressValidationProcessing$ = new BehaviorSubject(false);

  readonly #isInitialization$ = this.#_isInitialization$.asObservable().pipe(take(2));

  readonly #isTicketAssignmentStepVisible$ = this.#store.select(selectIsTicketAssignmentStepVisible);
  readonly #isTicketLimitPerEmailAddressActive$ = this.#store.select(selectIsTicketLimitPerEmailActive);
  readonly #groupedNameEmailAddressAndPersonsByTariffUuids$ = this.#store.select(
    selectGroupedNameEmailAddressAndPersonsByTariffUuids
  );

  readonly #checkTicketAssignmentTicketLimitPerEmailAddress$ = this.checkTicketAssignmentTicketLimitPerEmailAddress$();

  readonly #ticketLimitPerEmailAddressWarningMessageModalState = signal({ isOpen: false, isAlreadyShown: false });

  setIsTicketLimitPerEmailAddressValidationProcessing(isValidationProcessing: boolean) {
    this.#_isTicketLimitPerEmailAddressValidationProcessing$.next(isValidationProcessing);
  }

  setTicketLimitPerEmailAddressWarningMessageModalState(isOpen: boolean, isAlreadyShown: boolean) {
    return this.#ticketLimitPerEmailAddressWarningMessageModalState.set({ isOpen, isAlreadyShown });
  }

  resetIsTicketLimitPerEmailAddressValidationProcessing() {
    return this.#_isTicketLimitPerEmailAddressValidationProcessing$.next(false);
  }

  resetTicketLimitPerEmailAddressWarningMessageModalState() {
    return this.#ticketLimitPerEmailAddressWarningMessageModalState.set({ isOpen: false, isAlreadyShown: false });
  }

  getIsTicketLimitPerEmailAddressValidationProcessing$() {
    return this.#_isTicketLimitPerEmailAddressValidationProcessing$.asObservable().pipe(distinctUntilChanged());
  }

  getTicketLimitPerEmailAddressWarningMessageModalState() {
    return this.#ticketLimitPerEmailAddressWarningMessageModalState;
  }

  getCheckTicketAssignmentTicketLimitPerEmailAddress$() {
    return this.#checkTicketAssignmentTicketLimitPerEmailAddress$;
  }

  checkBuyerInformationTicketLimitPerEmailAddress$(
    buyerInformationForms$: Observable<CheckBuyerInformationFormsValidity>
  ) {
    const currentRouteStep$ = this.#router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(({ url }) => mapStepRouteToStepName(last(split(url, '/'))))
    );

    const bookedTariffUuids$ = this.#store.pipe(
      select(getAllTariffUuidsFromBookedProducts),
      distinctUntilChanged((previousBookedTariffUuids, currentBookedTariffUuids) =>
        isEqual(previousBookedTariffUuids, currentBookedTariffUuids)
      )
    );

    const isBuyerInformationStepChanges$ = combineLatest({
      currentRouteStep: currentRouteStep$,
      selectedStep: this.#store.select(getSelectedStep)
    }).pipe(
      filter(({ currentRouteStep, selectedStep }) => currentRouteStep === selectedStep),
      filter(({ selectedStep }) => !includes(DisableValidationOnSelectedSteps, selectedStep)),
      map(({ selectedStep }) => ({
        selectedStep,
        isBuyerInformationStep: selectedStep === StepName.BuyerInformation
      }))
    );

    const isBookedTariffsCountChangeOrIsBuyerInformationStep$ = combineLatest({
      bookedTariffUuids: bookedTariffUuids$,
      isBuyerInformationStepChanges: isBuyerInformationStepChanges$
    }).pipe(
      startWith({
        bookedTariffUuids: [],
        isBuyerInformationStepChanges: {
          selectedStep: null,
          isBuyerInformationStep: false
        }
      }),
      pairwise(),
      map(([previousChanges, currentChanges]) => {
        const {
          bookedTariffUuids: previousBookedTariffUuids,
          isBuyerInformationStepChanges: { selectedStep: previousSelectedStep }
        } = previousChanges;
        const {
          bookedTariffUuids: currentBookedTariffUuids,
          isBuyerInformationStepChanges: {
            selectedStep: currentSelectedStep,
            isBuyerInformationStep: currentIsBuyerInformationStep
          }
        } = currentChanges;

        const areBookedTariffUuidsChanged = !isEqual(previousBookedTariffUuids, currentBookedTariffUuids);
        const isSelectedStepChanged = previousSelectedStep !== currentSelectedStep;
        const isBookedProductsCountChanged = !isSelectedStepChanged ? areBookedTariffUuidsChanged : false;
        const isBuyerInformationStep = !isBookedProductsCountChanged ? currentIsBuyerInformationStep : false;

        return isBookedProductsCountChanged || isBuyerInformationStep;
      })
    );

    const buyerInformationFormGroup$ = buyerInformationForms$.pipe(
      map((buyerInformationForms) => buyerInformationForms[FormName.BuyerInformation].form.formGroup),
      filter((buyerInformationFormGroup) => !isNil(buyerInformationFormGroup)),
      distinctUntilChanged(
        (previousBuyerInformationFormGroup, currentBuyerInformationFormGroup) =>
          previousBuyerInformationFormGroup === currentBuyerInformationFormGroup
      )
    );

    return combineLatest({
      isInitialization: this.#isInitialization$,
      isTicketAssignmentStepVisible: this.#isTicketAssignmentStepVisible$,
      isTicketLimitPerEmailAddressActive: this.#isTicketLimitPerEmailAddressActive$,
      isBookedTariffsCountChangeOrIsBuyerInformationStep: isBookedTariffsCountChangeOrIsBuyerInformationStep$,
      buyerInformationFormGroup: buyerInformationFormGroup$
    }).pipe(
      tap(() => this.#_isInitialization$.next(false)),
      filter(
        ({ isTicketAssignmentStepVisible, isTicketLimitPerEmailAddressActive }) =>
          !isTicketAssignmentStepVisible && isTicketLimitPerEmailAddressActive
      ),
      map(({ isInitialization, isBookedTariffsCountChangeOrIsBuyerInformationStep, buyerInformationFormGroup }) => ({
        buyerInformationFormGroup,
        isInitialization$: isInitialization
          ? of(InitTicketLimitPerEmailAddressValidationTrigger.Initialization)
          : EMPTY,
        isBookedTariffsCountChangeOrIsBuyerInformationStep$: isBookedTariffsCountChangeOrIsBuyerInformationStep
          ? of(InitTicketLimitPerEmailAddressValidationTrigger.BookedTariffsCountChangeOrIsBuyerInformationStep)
          : EMPTY,
        emailAddressValueChanges$: buyerInformationFormGroup
          .get(FieldKey.Email)
          .valueChanges.pipe(distinctUntilChanged()),
        statusChanges$: buyerInformationFormGroup.statusChanges.pipe(distinctUntilChanged())
      })),
      switchMap(
        ({
          buyerInformationFormGroup,
          isInitialization$,
          isBookedTariffsCountChangeOrIsBuyerInformationStep$,
          emailAddressValueChanges$,
          statusChanges$
        }) =>
          merge(
            isInitialization$,
            isBookedTariffsCountChangeOrIsBuyerInformationStep$,
            emailAddressValueChanges$,
            statusChanges$
          ).pipe(
            map(() => buyerInformationFormGroup?.valid),
            tap((initTicketLimitPerEmailAddressValidation) =>
              this.setIsTicketLimitPerEmailAddressValidationProcessing(initTicketLimitPerEmailAddressValidation)
            ),
            debounce((initTicketLimitPerEmailAddressValidation) =>
              timer(initTicketLimitPerEmailAddressValidation ? 1000 : 0)
            ),
            withLatestFrom(buyerInformationForms$),
            map(([initTicketLimitPerEmailAddressValidation, buyerInformationForms]) => ({
              forms: buyerInformationForms,
              selectedStep: StepName.BuyerInformation,
              initTicketLimitPerEmailAddressValidation
            }))
          )
      ),
      this.checkTicketLimitPerEmailAddressPipe(StepName.BuyerInformation),
      tap(() => this.setIsTicketLimitPerEmailAddressValidationProcessing(false)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private checkTicketAssignmentTicketLimitPerEmailAddress$() {
    const ticketAssignmentForms$ = toObservable(
      computed(
        () =>
          ({
            [FormName.TicketHolder]: this.#formMediator.getFormState(FormName.TicketHolder)(),
            [FormName.TicketHolderQuestionnaire]: this.#formMediator.getFormState(FormName.TicketHolderQuestionnaire)()
          }) as CheckMergedTicketAssignmentFormsValidity
      )
    ).pipe(
      withLatestFrom(this.#store.select(selectTicketHolderBookedTariffsSortedByBookingOrder)),
      filter(([forms, ticketHolderBookedTariffs]) => {
        const ticketHolderForms = forms[FormName.TicketHolder];
        const areTicketHolderFormsInitialized = !isNil(ticketHolderForms) && !!ticketHolderForms.size;
        const ticketHolderFormsTariffUuids = areTicketHolderFormsInitialized ? [...ticketHolderForms.keys()] : [];
        const ticketHolderBookedTariffUuids = _map(ticketHolderBookedTariffs, ({ uuid }) => uuid);

        return (
          !isEmpty(ticketHolderFormsTariffUuids) &&
          !isEmpty(ticketHolderBookedTariffUuids) &&
          isEqual(ticketHolderFormsTariffUuids, ticketHolderBookedTariffUuids)
        );
      }),
      map(([forms]) => forms)
    );

    const ticketAssignmentFormsChanges$ = ticketAssignmentForms$.pipe(
      map((forms) => ({
        isCheckedTicketLimitPerEmailAddressCheck: false,
        forms,
        formErrors: {}
      }))
    );

    const checkTicketAssignmentTicketLimitPerEmailAddressChanges$ =
      this.checkTicketAssignmentTicketLimitPerEmailAddressChanges$(ticketAssignmentForms$).pipe(
        switchMap(({ forms, initTicketLimitPerEmailAddressValidation }) =>
          iif(
            () => initTicketLimitPerEmailAddressValidation,
            of({
              forms,
              selectedStep: StepName.TicketAssignment,
              initTicketLimitPerEmailAddressValidation
            }).pipe(this.checkTicketLimitPerEmailAddressPipe(StepName.TicketAssignment)),
            of({ forms, formErrors: {} })
          ).pipe(
            map(({ forms, formErrors }) => ({
              isCheckedTicketLimitPerEmailAddressCheck: initTicketLimitPerEmailAddressValidation,
              forms,
              formErrors
            }))
          )
        ),
        tap(() => this.setIsTicketLimitPerEmailAddressValidationProcessing(false)),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    return combineLatest({
      isTicketAssignmentStepVisible: this.#isTicketAssignmentStepVisible$,
      isTicketLimitPerEmailAddressActive: this.#isTicketLimitPerEmailAddressActive$
    }).pipe(
      withLatestFrom(this.#store.select(getSelectedStep)),
      filter(([{ isTicketAssignmentStepVisible }]) => isTicketAssignmentStepVisible),
      switchMap(([{ isTicketLimitPerEmailAddressActive }, selectedStep]) =>
        iif(
          () => includes(DisableValidationOnSelectedSteps, selectedStep) || !isTicketLimitPerEmailAddressActive,
          ticketAssignmentForms$.pipe(map((forms) => ({ forms, formErrors: {} }))),
          merge(ticketAssignmentFormsChanges$, checkTicketAssignmentTicketLimitPerEmailAddressChanges$).pipe(
            switchMap(({ isCheckedTicketLimitPerEmailAddressCheck, forms, formErrors }) =>
              iif(
                () => isCheckedTicketLimitPerEmailAddressCheck,
                of({ forms, formErrors }),
                of(forms).pipe(
                  withLatestFrom(checkTicketAssignmentTicketLimitPerEmailAddressChanges$),
                  map(([forms, { formErrors: ticketLimitPerEmailAddressFormErrors }]) => ({
                    forms,
                    formErrors: _merge({}, formErrors, ticketLimitPerEmailAddressFormErrors)
                  }))
                )
              )
            )
          )
        )
      )
    );
  }

  private checkTicketAssignmentTicketLimitPerEmailAddressChanges$(
    ticketAssignmentForms$: Observable<CheckMergedTicketAssignmentFormsValidity>
  ) {
    const selectedTariffUuid$ = this.#store.pipe(
      select(selectSelectedTariffUuid),
      filter((selectedTariffUuid) => !isNil(selectedTariffUuid))
    );

    const selectedTariffHolderUuid$ = this.#store.select(selectSelectedTariffHolderUuid);

    const waitForTicketHolderChangeOnSelectedTariffChange = 50;
    const selectedTariffAndTariffHolderUuidChanges$ = combineLatest({
      selectedTariffUuid: selectedTariffUuid$,
      selectedTariffHolderUuid: selectedTariffHolderUuid$
    }).pipe(debounceTime(waitForTicketHolderChangeOnSelectedTariffChange));

    const ticketAssignmentFormsChanges$ = ticketAssignmentForms$.pipe(
      distinctUntilChanged((previousTicketAssignmentForms, currentTicketAssignmentForms) => {
        const previousTicketHolderForms = previousTicketAssignmentForms[FormName.TicketHolder];
        const currentTicketHolderForms = currentTicketAssignmentForms[FormName.TicketHolder];
        const previousTicketHolderQuestionnaireForms =
          previousTicketAssignmentForms[FormName.TicketHolderQuestionnaire];
        const currentTicketHolderQuestionnaireForms = currentTicketAssignmentForms[FormName.TicketHolderQuestionnaire];

        const previousTicketHolderFormsSize = previousTicketHolderForms.size;
        const currentTicketHolderFormsSize = currentTicketHolderForms.size;

        const previousTicketHolderQuestionnaireFormsSize = previousTicketHolderQuestionnaireForms.size;
        const currentTicketHolderQuestionnaireFormsSize = currentTicketHolderQuestionnaireForms.size;

        const previousTicketHolderUuids = _map([...previousTicketHolderForms.values()], 'person.uuid');
        const currentTicketHolderUuids = _map([...currentTicketHolderForms.values()], 'person.uuid');

        return (
          previousTicketHolderFormsSize === currentTicketHolderFormsSize &&
          previousTicketHolderQuestionnaireFormsSize === currentTicketHolderQuestionnaireFormsSize &&
          isEqual(previousTicketHolderUuids, currentTicketHolderUuids)
        );
      })
    );

    const initialState = {
      isInitialization: false,
      isTicketAssignmentStepVisible: false,
      isTicketLimitPerEmailAddressActive: false,
      selectedTariffAndTariffHolderUuidChanges: { selectedTariffUuid: null, selectedTariffHolderUuid: undefined },
      ticketAssignmentForms: {
        [FormName.TicketHolder]: new Map(),
        [FormName.TicketHolderQuestionnaire]: new Map()
      } as CheckMergedTicketAssignmentFormsValidity
    };

    return combineLatest({
      isInitialization: this.#isInitialization$,
      isTicketAssignmentStepVisible: this.#isTicketAssignmentStepVisible$,
      isTicketLimitPerEmailAddressActive: this.#isTicketLimitPerEmailAddressActive$,
      selectedTariffAndTariffHolderUuidChanges: selectedTariffAndTariffHolderUuidChanges$,
      ticketAssignmentForms: ticketAssignmentFormsChanges$
    }).pipe(
      startWith(initialState),
      pairwise(),
      filter(([_, { isTicketLimitPerEmailAddressActive }]) => isTicketLimitPerEmailAddressActive),
      tap(() => this.#_isInitialization$.next(false)),
      map(([previousChanges, currentChanges]) => {
        const {
          isTicketAssignmentStepVisible: previousIsTicketAssignmentStepVisible,
          ticketAssignmentForms: previousTicketAssignmentForms,
          selectedTariffAndTariffHolderUuidChanges: {
            selectedTariffUuid: previousSelectedTariffUuid,
            selectedTariffHolderUuid: previousSelectedTariffHolderUuid
          }
        } = previousChanges;
        const {
          isInitialization,
          isTicketAssignmentStepVisible: currentIsTicketAssignmentStepVisible,
          ticketAssignmentForms: currentTicketAssignmentForms,
          selectedTariffAndTariffHolderUuidChanges: {
            selectedTariffUuid: currentSelectedTariffUuid,
            selectedTariffHolderUuid: currentSelectedTariffHolderUuid
          }
        } = currentChanges;

        const currentTicketHolderForms = currentTicketAssignmentForms[FormName.TicketHolder];
        const currentTicketHolderQuestionnaireForms = currentTicketAssignmentForms[FormName.TicketHolderQuestionnaire];

        const ticketHolderForms = currentTicketHolderForms;
        const ticketHolderQuestionnaireForms = currentTicketHolderQuestionnaireForms;
        const selectedTicketHolderFormState = ticketHolderForms.get(currentSelectedTariffUuid);
        const selectedTicketHolderQuestionnaireFormState =
          ticketHolderQuestionnaireForms.get(currentSelectedTariffUuid);

        const isTicketAssignmentStepSetToVisible =
          previousIsTicketAssignmentStepVisible !== currentIsTicketAssignmentStepVisible &&
          currentIsTicketAssignmentStepVisible;

        const isInitializationOrTicketAssignmentStepIsSetToVisible =
          isInitialization || isTicketAssignmentStepSetToVisible;

        const isTicketAssignmentFormsCountChanged = isInitializationOrTicketAssignmentStepIsSetToVisible
          ? false
          : previousTicketAssignmentForms[FormName.TicketHolder].size !== currentTicketHolderForms.size;

        const isSelectedTariffUuidChanged = isInitializationOrTicketAssignmentStepIsSetToVisible
          ? false
          : previousSelectedTariffUuid !== currentSelectedTariffUuid;

        const isSelectedTariffHolderUuidChanged =
          isInitializationOrTicketAssignmentStepIsSetToVisible || isSelectedTariffUuidChanged
            ? false
            : previousSelectedTariffHolderUuid !== currentSelectedTariffHolderUuid;

        const isTicketHolderAssignedToMultipleFormsUnassignedFromForm = !isSelectedTariffHolderUuidChanged
          ? false
          : some(
              [...currentTicketHolderForms.values()],
              ({ person }) => person?.uuid === previousSelectedTariffHolderUuid
            );

        return {
          isInitialization,
          isTicketAssignmentStepSetToVisible,
          ticketAssignmentForms: currentTicketAssignmentForms,
          selectedTariffUuid: currentSelectedTariffUuid,
          selectedTariffHolderUuid: currentSelectedTariffHolderUuid,
          ticketHolderForms,
          ticketHolderQuestionnaireForms,
          selectedTicketHolderFormState,
          selectedTicketHolderQuestionnaireFormState,
          isTicketAssignmentFormsCountChanged,
          isSelectedTariffUuidChanged,
          isSelectedTariffHolderUuidChanged,
          isTicketHolderAssignedToMultipleFormsUnassignedFromForm
        };
      }),
      filter(
        ({ isTicketAssignmentStepSetToVisible, selectedTariffHolderUuid, selectedTicketHolderFormState }) =>
          isTicketAssignmentStepSetToVisible || selectedTicketHolderFormState?.person?.uuid === selectedTariffHolderUuid
      ),
      map(
        ({
          isInitialization,
          isTicketAssignmentStepSetToVisible,
          ticketAssignmentForms,
          ticketHolderForms,
          ticketHolderQuestionnaireForms,
          selectedTicketHolderFormState,
          selectedTicketHolderQuestionnaireFormState,
          isTicketAssignmentFormsCountChanged,
          isSelectedTariffUuidChanged,
          isSelectedTariffHolderUuidChanged,
          isTicketHolderAssignedToMultipleFormsUnassignedFromForm
        }) => {
          const isInitialization$ = isInitialization
            ? of(InitTicketLimitPerEmailAddressValidationTrigger.Initialization)
            : EMPTY;

          const isTicketAssignmentStepSetToVisible$ = isTicketAssignmentStepSetToVisible
            ? of(InitTicketLimitPerEmailAddressValidationTrigger.TicketAssignmentStepSetToVisible)
            : EMPTY;

          const isTicketAssignmentFormsCountChanged$ = isTicketAssignmentFormsCountChanged
            ? of(InitTicketLimitPerEmailAddressValidationTrigger.TicketAssignmentFormsCountChanged)
            : EMPTY;

          const isSelectedTariffUuidChanged$ = isSelectedTariffUuidChanged
            ? of(InitTicketLimitPerEmailAddressValidationTrigger.SelectedTariffUuidChanged)
            : EMPTY;

          const isSelectedTicketHolderUuidChanged$ = isSelectedTariffHolderUuidChanged
            ? of(InitTicketLimitPerEmailAddressValidationTrigger.SelectedTicketHolderUuidChanged)
            : EMPTY;

          const isTicketHolderAssignedToMultipleFormsUnassignedFromForm$ =
            isTicketHolderAssignedToMultipleFormsUnassignedFromForm
              ? of(
                  InitTicketLimitPerEmailAddressValidationTrigger.TicketHolderAssignedToMultipleFormsUnassignedFromForm
                )
              : EMPTY;

          const { ticketHolderEmailAddressValueChanges, ticketHolderFormsStatusChanges } = reduce(
            [...ticketHolderForms.values()],
            (ticketHolderFormChanges, ticketHolderFormState) => {
              const {
                form: { formGroup }
              } = ticketHolderFormState;
              const { ticketHolderEmailAddressValueChanges, ticketHolderFormsStatusChanges } = ticketHolderFormChanges;

              const emailAddressValueChanges = formGroup.get(FieldKey.Email)?.valueChanges;

              if (!isNil(emailAddressValueChanges)) {
                ticketHolderEmailAddressValueChanges.push(emailAddressValueChanges);
              }

              if (!isNil(formGroup?.statusChanges)) {
                ticketHolderFormsStatusChanges.push(formGroup.statusChanges);
              }

              return ticketHolderFormChanges;
            },
            {
              ticketHolderEmailAddressValueChanges: [],
              ticketHolderFormsStatusChanges: []
            }
          );

          const ticketHolderQuestionnaireFormStateChanges = _map(
            [...ticketHolderQuestionnaireForms.values()],
            ({ form }) => form.formGroup.statusChanges
          );

          const ticketHolderEmailAddressValueChanges$ = merge(...ticketHolderEmailAddressValueChanges).pipe(
            distinctUntilChanged()
          );
          const ticketHolderFormsStatusChanges$ = merge(...ticketHolderFormsStatusChanges).pipe(distinctUntilChanged());
          const ticketHolderQuestionnaireFormsStatusChanges$ = merge(...ticketHolderQuestionnaireFormStateChanges).pipe(
            distinctUntilChanged()
          );

          return {
            ticketAssignmentForms,
            selectedTicketHolderFormState,
            selectedTicketHolderQuestionnaireFormState,
            isInitialization$,
            isTicketAssignmentStepSetToVisible$,
            isTicketAssignmentFormsCountChanged$,
            isSelectedTariffUuidChanged$,
            isSelectedTicketHolderUuidChanged$,
            isTicketHolderAssignedToMultipleFormsUnassignedFromForm$,
            ticketHolderEmailAddressValueChanges$,
            ticketHolderFormsStatusChanges$,
            ticketHolderQuestionnaireFormsStatusChanges$
          };
        }
      ),
      switchMap(
        ({
          ticketAssignmentForms,
          selectedTicketHolderFormState,
          selectedTicketHolderQuestionnaireFormState,
          isInitialization$,
          isTicketAssignmentStepSetToVisible$,
          isTicketAssignmentFormsCountChanged$,
          isSelectedTariffUuidChanged$,
          isSelectedTicketHolderUuidChanged$,
          isTicketHolderAssignedToMultipleFormsUnassignedFromForm$,
          ticketHolderEmailAddressValueChanges$,
          ticketHolderFormsStatusChanges$,
          ticketHolderQuestionnaireFormsStatusChanges$
        }) =>
          merge(
            isInitialization$,
            isTicketAssignmentStepSetToVisible$,
            isTicketAssignmentFormsCountChanged$,
            isSelectedTariffUuidChanged$,
            isSelectedTicketHolderUuidChanged$,
            isTicketHolderAssignedToMultipleFormsUnassignedFromForm$,
            ticketHolderEmailAddressValueChanges$,
            ticketHolderFormsStatusChanges$,
            ticketHolderQuestionnaireFormsStatusChanges$
          ).pipe(
            map((change: string | FormControlStatus | typeof InitTicketLimitPerEmailAddressValidationTrigger) => {
              const isTicketLimitPerEmailAddressValidationTrigger = includes(
                [
                  InitTicketLimitPerEmailAddressValidationTrigger.Initialization,
                  InitTicketLimitPerEmailAddressValidationTrigger.TicketAssignmentStepSetToVisible,
                  InitTicketLimitPerEmailAddressValidationTrigger.TicketAssignmentFormsCountChanged,
                  InitTicketLimitPerEmailAddressValidationTrigger.SelectedTariffUuidChanged,
                  InitTicketLimitPerEmailAddressValidationTrigger.SelectedTicketHolderUuidChanged
                ],
                change
              );

              const isSelectedTicketHolderFormValid = selectedTicketHolderFormState?.form?.formGroup?.valid;
              const isSelectedTicketHolderQuestionnaireFormValid =
                isNil(selectedTicketHolderQuestionnaireFormState) ||
                selectedTicketHolderQuestionnaireFormState?.form?.formGroup?.valid;
              const areAllSelectedFormsValid =
                isSelectedTicketHolderFormValid && isSelectedTicketHolderQuestionnaireFormValid;
              const isTicketHolderAssignedToMultipleFormsUnassignedFromForm =
                change ===
                InitTicketLimitPerEmailAddressValidationTrigger.TicketHolderAssignedToMultipleFormsUnassignedFromForm;

              return (
                isTicketLimitPerEmailAddressValidationTrigger ||
                isTicketHolderAssignedToMultipleFormsUnassignedFromForm ||
                areAllSelectedFormsValid
              );
            }),
            tap((initTicketLimitPerEmailAddressValidation) =>
              this.setIsTicketLimitPerEmailAddressValidationProcessing(initTicketLimitPerEmailAddressValidation)
            ),
            debounce((initTicketLimitPerEmailAddressValidation) =>
              timer(initTicketLimitPerEmailAddressValidation ? 1000 : 0)
            ),
            tap((initTicketLimitPerEmailAddressValidation) => {
              if (initTicketLimitPerEmailAddressValidation) {
                return;
              }

              this.setIsTicketLimitPerEmailAddressValidationProcessing(false);
            }),
            filter((initTicketLimitPerEmailAddressValidation) => initTicketLimitPerEmailAddressValidation),
            map((initTicketLimitPerEmailAddressValidation) => ({
              forms: ticketAssignmentForms,
              initTicketLimitPerEmailAddressValidation
            }))
          )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private checkTicketLimitPerEmailAddressPipe<Forms>(
    checkValidityOnStep: StepName
  ): UnaryFunction<
    Observable<{ forms: Forms; selectedStep: StepName; initTicketLimitPerEmailAddressValidation: boolean }>,
    Observable<{ forms: Forms; formErrors: CheckedTariffEmailAddressValidity }>
  > {
    return pipe(
      withLatestFrom(this.#store.select(getExhibitionSettings)),
      filter(([_, exhibitionSettings]) => !isEmpty(exhibitionSettings)),
      switchMap(([{ forms, selectedStep, initTicketLimitPerEmailAddressValidation }, exhibitionSettings]) =>
        iif(
          () => !initTicketLimitPerEmailAddressValidation || !exhibitionSettings.ticketLimitPerEmail,
          of({ forms, formErrors: {} }),
          combineLatest({
            selectedTariffUuid: this.#store.select(selectSelectedTariffUuid),
            isSelfRegistration: this.#store.select(getSelfRegistration),
            buyerEmailAddress: this.#store.select(selectBuyerInformationEmailAddress),
            allTicketHolderTariffUuidsWithTicketPersonId: this.#store.select(
              selectAllTicketHolderTariffUuidsWithTicketPersonId
            ),
            isTicketAssignmentStepVisible: this.#store.select(selectIsTicketAssignmentStepVisible)
          }).pipe(
            first(),
            switchMap(
              ({
                isSelfRegistration,
                buyerEmailAddress,
                allTicketHolderTariffUuidsWithTicketPersonId,
                isTicketAssignmentStepVisible
              }) =>
                iif(
                  () => checkValidityOnStep === StepName.BuyerInformation && isTicketAssignmentStepVisible,
                  of({}),
                  iif(
                    () => selectedStep === StepName.BuyerInformation,
                    iif(
                      () => !isEmpty(buyerEmailAddress) && isNull(validateEmailAddress(buyerEmailAddress)),
                      of(allTicketHolderTariffUuidsWithTicketPersonId).pipe(
                        map(
                          (
                            allTicketHolderTariffUuidsWithTicketPersonId
                          ): Record<TariffUuid, { email: string; ticketPersonId: TicketPersonId }> =>
                            mapValues(allTicketHolderTariffUuidsWithTicketPersonId, (ticketPersonId) => ({
                              email: buyerEmailAddress,
                              ticketPersonId
                            }))
                        )
                      ),
                      of({})
                    ),
                    this.#groupedNameEmailAddressAndPersonsByTariffUuids$.pipe(
                      first(),
                      switchMap((groupedNameEmailAddressAndPersonsByTariffUuids) =>
                        iif(
                          () => isEmpty(groupedNameEmailAddressAndPersonsByTariffUuids),
                          of({}),
                          of(groupedNameEmailAddressAndPersonsByTariffUuids).pipe(
                            map((groupedNameEmailAddressAndPersonsByTariffUuids) =>
                              this.assignBuyerEmailToAssignedHoldersWithoutEmailAddress(
                                forms[FormName.TicketHolder],
                                buyerEmailAddress,
                                allTicketHolderTariffUuidsWithTicketPersonId,
                                groupedNameEmailAddressAndPersonsByTariffUuids
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                ).pipe(
                  map((emailAddressesWithTicketPersonIds) => {
                    const validateDailyTicketPerEmailLimitBody: ValidateDailyTicketPerEmailLimitBody = {
                      eventId: exhibitionSettings.eventId,
                      isSelfRegistration,
                      items: _map(values(emailAddressesWithTicketPersonIds), ({ ticketPersonId, email }, index) => ({
                        index,
                        email,
                        ticketPersonId,
                        amount: 1
                      }))
                    };

                    return {
                      ticketLimitPerEmail: exhibitionSettings.ticketLimitPerEmail,
                      emailAddressesWithTicketPersonIds,
                      validateDailyTicketPerEmailLimitBody
                    };
                  }),
                  switchMap(
                    ({
                      ticketLimitPerEmail,
                      emailAddressesWithTicketPersonIds,
                      validateDailyTicketPerEmailLimitBody
                    }) =>
                      iif(
                        () => isEmpty(validateDailyTicketPerEmailLimitBody.items),
                        of({ forms, formErrors: {} }),
                        this.#tariffStatusService
                          .checkDailyTicketPerEmailLimit$(validateDailyTicketPerEmailLimitBody)
                          .pipe(
                            map((validateDailyTicketPerEmailLimitResult) => ({
                              forms,
                              formErrors: mapValidatedDailyTicketPerLimitResponseToCheckedValidity(
                                ticketLimitPerEmail,
                                emailAddressesWithTicketPersonIds,
                                validateDailyTicketPerEmailLimitResult
                              )
                            })),
                            catchError(() => of({ forms, formErrors: {} })),
                            tap(({ formErrors }) =>
                              this.#ticketLimitPerEmailAddressWarningMessageModalState.update(({ isAlreadyShown }) => ({
                                isOpen: !isEmpty(formErrors),
                                isAlreadyShown
                              }))
                            )
                          )
                      )
                  )
                )
            )
          )
        )
      ),
      shareReplay(1)
    );
  }

  private assignBuyerEmailToAssignedHoldersWithoutEmailAddress(
    ticketHolderFormStates: TicketHolderFormStates,
    buyerEmailAddress: string,
    allTicketHolderTariffUuidsWithTicketPersonId: Record<TariffUuid, TicketPersonId>,
    groupedNameEmailAddressAndPersonsByTariffUuids: GroupedNameEmailAddressAndPersonsByTariffUuids
  ): Record<TariffUuid, { email: string; ticketPersonId: TicketPersonId }> {
    return omitBy(
      mapValues(groupedNameEmailAddressAndPersonsByTariffUuids, (item, tariffUuid) => {
        if (isEmpty(item.person)) {
          return null;
        }

        if (item.person?.type === PersonType.Holder && isEmpty(item.email)) {
          item.email = buyerEmailAddress;
        }

        if (item.email !== buyerEmailAddress && validateEmailAddress(item.email)) {
          return null;
        }

        const ticketPersonId = allTicketHolderTariffUuidsWithTicketPersonId[tariffUuid];

        if (isNil(ticketPersonId)) {
          return null;
        }

        const isTicketHolderFormValid = ticketHolderFormStates.get(tariffUuid).form.formGroup.valid;

        if (!isTicketHolderFormValid) {
          return null;
        }

        return {
          email: item.email,
          ticketPersonId
        };
      }),
      isNull
    );
  }
}
